<template>
  <div class="pipeline-insights-root">
    <b-card no-body class="pipeline-shell-card">
      <b-card-header class="pipeline-page-header">
        <div class="d-flex align-items-center pipeline-title-block">
          <div class="pipeline-icon-wrap">
            <feather-icon icon="TrendingUpIcon" size="20" class="text-white" />
          </div>
          <div class="pipeline-heading-text">
            <h4 class="mb-0 pipeline-title">{{ $t("Enrolment overview") }}</h4>
            <small class="pipeline-subtitle">{{
              $t("Inquiries, admissions and registrations at a glance")
            }}</small>
          </div>
        </div>
        <div class="pipeline-filter-row d-flex flex-wrap align-items-center">
          <b-button
            v-if="pipelineTotalSelectionCount > 0"
            variant="gradient-primary"
            size="sm"
            class="pipeline-filter-send-selected flex-shrink-0 mr-2 mt-1 mt-lg-0"
            :aria-label="
              $t('Bulk send') + ' (' + pipelineTotalSelectionCount + ')'
            "
            @click="openBulkComposerAllSelected"
          >
            <feather-icon icon="MailIcon" size="14" class="pipeline-filter-send-selected__icon" />
            <span class="pipeline-filter-send-selected__label"
              >{{ $t("Bulk send") }} ({{ pipelineTotalSelectionCount }})</span
            >
          </b-button>
          <b-button
            v-if="pipelineTotalSelectionCount > 0"
            variant="outline-secondary"
            class="pipeline-filter-clear-all btn-icon flex-shrink-0 mr-2 mt-1 mt-lg-0"
            :aria-label="$t('Clear')"
            :title="$t('Clear selection in all columns')"
            @click="clearPipelineSelection"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
          <b-form-group
            :label="$t('From')"
            class="mb-0 mr-2 pipeline-date-group d-flex align-items-center"
            label-cols="auto"
          >
            <flat-pickr
              v-model="dateFromDay"
              :config="pipelineDatePickrConfig"
              :placeholder="$t('From date')"
            />
          </b-form-group>
          <b-form-group
            :label="$t('To')"
            class="mb-0 mr-2 pipeline-date-group d-flex align-items-center"
            label-cols="auto"
          >
            <flat-pickr
              v-model="dateTillDay"
              :config="pipelineDatePickrConfig"
              :placeholder="$t('To date')"
            />
          </b-form-group>
        </div>
      </b-card-header>

      <b-card-body class="pipeline-body px-2 pb-2">
        <div v-if="loading" class="pipeline-skeleton-container py-2">
          <!-- Skeleton Stats Row -->
          <b-row class="mb-2">
            <b-col v-for="i in 3" :key="'skel-stat-' + i" cols="12" md="4" class="mb-1">
              <div class="pipeline-skeleton-item skeleton-pulse" style="height: 80px; border-radius: 12px;"></div>
            </b-col>
          </b-row>
          <!-- Skeleton Columns Row -->
          <b-row>
            <b-col v-for="i in 3" :key="'skel-col-' + i" cols="12" lg="4" class="mb-2">
              <div class="pipeline-skeleton-item skeleton-pulse mb-1" style="height: 40px; border-radius: 8px;"></div>
              <div v-for="j in 3" :key="'skel-row-' + i + '-' + j" class="pipeline-skeleton-item skeleton-pulse mb-1" style="height: 120px; border-radius: 10px;"></div>
            </b-col>
          </b-row>
        </div>

        <template v-else-if="pipeline">
          <!-- Stat cards -->
          <b-row class="pipeline-stats-row mb-2">
            <b-col cols="12" md="4" class="mb-2 mb-md-0">
              <div class="pipeline-stat-card pipeline-stat-card--inquiry">
                <div class="pipeline-stat-card__icon">
                  <feather-icon icon="HelpCircleIcon" size="22" />
                </div>
                <div class="pipeline-stat-card__body">
                  <span class="pipeline-stat-card__label">{{ $t("Inquiries") }}</span>
                  <span class="pipeline-stat-card__value">{{ inquiryCount }}</span>
                </div>
              </div>
            </b-col>
            <b-col cols="12" md="4" class="mb-2 mb-md-0">
              <div class="pipeline-stat-card pipeline-stat-card--registration">
                <div class="pipeline-stat-card__icon">
                  <feather-icon icon="Edit3Icon" size="22" />
                </div>
                <div class="pipeline-stat-card__body">
                  <span class="pipeline-stat-card__label">{{ $t("Registrations") }}</span>
                  <span class="pipeline-stat-card__value">{{ registrationCount }}</span>
                </div>
              </div>
            </b-col>
            <b-col cols="12" md="4">
              <div class="pipeline-stat-card pipeline-stat-card--admission">
                <div class="pipeline-stat-card__icon">
                  <feather-icon icon="UserCheckIcon" size="22" />
                </div>
                <div class="pipeline-stat-card__body">
                  <span class="pipeline-stat-card__label">{{ $t("Admissions") }}</span>
                  <span class="pipeline-stat-card__value">{{ admissionCount }}</span>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Lists -->
          <b-row class="pipeline-columns-row">
            <b-col cols="12" lg="4" class="mb-2">
              <div class="pipeline-column-card">
                <div class="pipeline-column-head">
                  <div
                    class="pipeline-column-heading-main d-flex align-items-center flex-wrap flex-grow-1 min-width-0"
                  >
                    <feather-icon icon="MessageSquareIcon" size="16" class="mr-50 flex-shrink-0" />
                    <span class="pipeline-column-title pipeline-column-title--inline">{{
                      $t("Inquiries")
                    }}</span>
                    <span
                      :id="inquiryPriorityLegendTargetId"
                      class="pipeline-priority-legend-trigger flex-shrink-0"
                      role="button"
                      tabindex="0"
                      :aria-label="$t('How inquiry priority labels work')"
                    >
                      <feather-icon icon="InfoIcon" size="15" />
                    </span>
                  </div>
                  <span class="pipeline-column-count flex-shrink-0">{{
                    filteredInquiries.length
                  }}</span>
                  <b-popover
                    :target="inquiryPriorityLegendTargetId"
                    triggers="hover focus"
                    placement="bottom"
                    boundary="viewport"
                    custom-class="pipeline-priority-legend-popover"
                  >
                    <template #title>{{ $t("How inquiry priorities work") }}</template>
                    <ul class="list-unstyled mb-0 pipeline-priority-legend-list">
                      <li
                        v-for="line in inquiryPriorityLegendLines"
                        :key="line.key"
                        class="pipeline-priority-legend-item"
                      >
                        <div class="pipeline-priority-legend-label">
                          <span class="pipeline-priority-legend-emoji">{{
                            line.emoji
                          }}</span>
                          {{ line.title }}
                        </div>
                        <ul class="pipeline-priority-legend-bullets">
                          <li
                            v-for="(criterion, ci) in line.criteria"
                            :key="line.key + '-c-' + ci"
                          >
                            {{ criterion }}
                          </li>
                        </ul>
                        <div class="pipeline-priority-legend-action">
                          <strong>{{ $t("Action") }}:</strong>
                          {{ line.action }}
                        </div>
                      </li>
                    </ul>
                  </b-popover>
                </div>
                <div class="pipeline-column-filters d-flex align-items-center px-1 py-50 bg-light">
                  <b-form-checkbox
                    class="pipeline-column-select-all mb-0 flex-shrink-0"
                    :checked="pipelineColumnAllSelected('inq')"
                    :indeterminate="pipelineColumnSomeSelected('inq')"
                    :disabled="filteredInquiries.length === 0"
                    v-b-tooltip.hover
                    :title="$t('Select all')"
                    @change="togglePipelineSelectAll('inq', $event)"
                  />
                  <flat-pickr
                    v-model="followUpDateFrom"
                    :config="columnDateConfig"
                    placeholder="From"
                    class="form-control form-control-sm mr-25 flex-grow-1"
                    @on-change="fetchPipeline"
                  />
                  <span class="mx-25 text-muted small flex-shrink-0">—</span>
                  <flat-pickr
                    v-model="followUpDateTill"
                    :config="columnDateConfig"
                    placeholder="Till"
                    class="form-control form-control-sm ml-25 flex-grow-1"
                    @on-change="fetchPipeline"
                  />
                </div>
                <div class="pipeline-column-body">
                  <div
                    v-if="filteredInquiries.length === 0"
                    class="pipeline-empty"
                  >
                    {{ $t("No inquiries in this range.") }}
                  </div>
                  <div v-else class="pipeline-scroll">
                    <div
                      v-for="(row, idx) in filteredInquiries"
                      :key="'inq-' + idx + '-' + (row.id || row.contact)"
                      class="pipeline-row-item pipeline-row-item--student pipeline-row-item--inquiry"
                    >
                      <b-form-checkbox
                        class="pipeline-row-checkbox flex-shrink-0"
                        :checked="isPipelineSelected('inq', row, idx)"
                        @input="setPipelineSelected('inq', row, idx, $event)"
                      />
                      <div class="pipeline-student-block min-width-0">
                        <div class="d-flex align-items-center justify-content-between mb-25 pr-3">
                          <div
                            class="d-flex align-items-center min-width-0 pipeline-inquiry-title-stack"
                          >
                            <span class="pipeline-row-name text-truncate">{{
                              row.name || "—"
                            }}</span>
                            <span
                              v-if="row.priority"
                              class="pipeline-inq-priority-tag"
                              :class="
                                'pipeline-inq-priority-tag--' +
                                inquiryPrioritySlug(row.priority)
                              "
                              v-b-tooltip.hover="inquiryFollowupTooltip(row)"
                              role="status"
                            >
                              {{ inquiryPriorityLabel(row.priority) }}
                            </span>
                          </div>
                          <span class="pipeline-row-type-badge">{{ row.inquiryType || $t('Inquiry') }}</span>
                        </div>

                        <div class="d-flex flex-wrap align-items-center mt-25">
                          <div v-if="pipelineContactPhone(row)" class="pipeline-row-chips mr-50">
                            <span class="pipeline-row-chip pipeline-row-chip--phone text-truncate" :title="pipelineContactPhone(row)">
                              {{ pipelineContactPhone(row) }}
                            </span>
                          </div>
                          <div v-if="row.email" class="d-flex align-items-center mr-50">
                            <feather-icon icon="MailIcon" size="12" class="text-muted mr-25" />
                            <span class="text-secondary small text-truncate" style="max-width: 120px" :title="row.email">{{ row.email }}</span>
                          </div>
                        </div>

                        <div v-if="row.fatherName" class="pipeline-compact-info mt-50">
                          <feather-icon icon="UserIcon" size="12" class="text-muted mr-25" />
                          <span class="pipeline-info-label small">{{ $t('Father') }}:</span>
                          <span class="pipeline-info-value small ml-25">{{ row.fatherName }}</span>
                        </div>

                        <div
                          v-if="row.inquiryDate || row.nextFollowup"
                          class="pipeline-timeline pipeline-timeline-one-line mt-50"
                        >
                          <span
                            v-if="row.inquiryDate"
                            class="pipeline-timeline-item pipeline-timeline-item--inline"
                          >
                            <span class="pipeline-timeline-dot pipeline-timeline-dot--primary"></span>
                            <span class="pipeline-timeline-label small font-weight-bolder">{{ $t('Inquiry') }}:</span>
                            <span class="pipeline-timeline-value small ml-1">{{ formatShortDate(row.inquiryDate) }}</span>
                          </span>
                          <span
                            v-if="row.inquiryDate && row.nextFollowup"
                            class="pipeline-timeline-inline-sep small text-muted mx-1 flex-shrink-0"
                            aria-hidden="true"
                          >·</span>
                          <span
                            v-if="row.nextFollowup"
                            class="pipeline-timeline-item pipeline-timeline-item--inline"
                          >
                            <span class="pipeline-timeline-dot pipeline-timeline-dot--warning"></span>
                            <span class="pipeline-timeline-label small font-weight-bolder">{{ $t('Follow-up') }}:</span>
                            <span class="pipeline-timeline-value small ml-1">{{ formatShortDate(row.nextFollowup) }}</span>
                          </span>
                        </div>

                        <div v-if="inquiryMessagePreview(row)" class="pipeline-row-message mt-50 text-truncate" :title="inquiryMessagePreview(row)">
                          {{ inquiryMessagePreview(row) }}
                        </div>
                      </div>
                      <div class="pipeline-row-actions flex-shrink-0">
                        <b-button
                          variant="outline-primary"
                          class="pipeline-mail-btn btn-icon rounded-circle"
                          :disabled="!canSendPipelineMessage(row)"
                          v-b-tooltip.hover.top
                          :title="
                            canSendPipelineMessage(row)
                              ? $t('Send message')
                              : $t('No phone or linked student for messaging')
                          "
                          @click="openStudentMessageSidebar(row)"
                        >
                          <feather-icon icon="MailIcon" size="16" />
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" lg="4" class="mb-2">
              <div class="pipeline-column-card">
                <div class="pipeline-column-head">
                  <feather-icon icon="ClipboardIcon" size="16" class="mr-50" />
                  <span class="pipeline-column-title">{{ $t("Registrations") }}</span>
                  <span class="pipeline-column-count">{{ filteredRegistrations.length }}</span>
                </div>
                <div class="pipeline-column-filters d-flex align-items-center px-1 py-50 bg-light">
                  <b-form-checkbox
                    class="pipeline-column-select-all mb-0 flex-shrink-0"
                    :checked="pipelineColumnAllSelected('reg')"
                    :indeterminate="pipelineColumnSomeSelected('reg')"
                    :disabled="filteredRegistrations.length === 0"
                    v-b-tooltip.hover
                    :title="$t('Select all')"
                    @change="togglePipelineSelectAll('reg', $event)"
                  />
                  <flat-pickr
                    v-model="registrationDateFrom"
                    :config="columnDateConfig"
                    placeholder="From"
                    class="form-control form-control-sm mr-25 flex-grow-1"
                    @on-change="fetchPipeline"
                  />
                  <span class="mx-25 text-muted small flex-shrink-0">—</span>
                  <flat-pickr
                    v-model="registrationDateTill"
                    :config="columnDateConfig"
                    placeholder="Till"
                    class="form-control form-control-sm ml-25 flex-grow-1"
                    @on-change="fetchPipeline"
                  />
                </div>
                <div class="pipeline-column-body">
                  <div
                    v-if="filteredRegistrations.length === 0"
                    class="pipeline-empty"
                  >
                    {{ $t("No registrations in this range.") }}
                  </div>
                  <div v-else class="pipeline-scroll">
                    <div
                      v-for="(row, idx) in filteredRegistrations"
                      :key="'reg-' + idx + '-' + row.studentId"
                      class="pipeline-row-item pipeline-row-item--student"
                    >
                      <b-form-checkbox
                        class="pipeline-row-checkbox flex-shrink-0"
                        :checked="isPipelineSelected('reg', row, idx)"
                        @input="setPipelineSelected('reg', row, idx, $event)"
                      />
                      <div class="pipeline-student-block min-width-0">
                        <div class="d-flex align-items-center justify-content-between mb-25 pr-3">
                          <span class="pipeline-row-name text-truncate">{{ row.name || "—" }}</span>
                          <span class="pipeline-row-type-badge pipeline-row-type-badge--registration">{{ row.className || $t('Registration') }}</span>
                        </div>

                        <div class="d-flex flex-wrap align-items-center mt-25">
                          <div v-if="row.grNo" class="pipeline-row-chips mr-50">
                            <span class="pipeline-row-chip pipeline-row-chip--gr">{{ $t("GR") }} {{ row.grNo }}</span>
                          </div>
                          <div v-if="pipelineContactPhone(row)" class="pipeline-row-chips mr-50">
                            <span class="pipeline-row-chip pipeline-row-chip--phone text-truncate" :title="pipelineContactPhone(row)">
                              {{ pipelineContactPhone(row) }}
                            </span>
                          </div>
                        </div>

                        <div v-if="row.fatherName || row.contactFather" class="pipeline-compact-info mt-50">
                          <div v-if="row.fatherName" class="d-inline-flex align-items-center mr-1">
                            <feather-icon icon="UserIcon" size="12" class="text-muted mr-25" />
                            <span class="pipeline-info-value small">{{ row.fatherName }}</span>
                          </div>
                          <div v-if="row.contactFather" class="d-inline-flex align-items-center">
                            <feather-icon icon="PhoneIcon" size="12" class="text-muted mr-25" />
                            <span class="pipeline-info-value small">{{ row.contactFather }}</span>
                          </div>
                        </div>

                        <div class="pipeline-timeline horizontal mt-50">
                          <div v-if="row.registrationDate" class="pipeline-timeline-item">
                            <span class="pipeline-timeline-dot pipeline-timeline-dot--info"></span>
                            <span class="pipeline-timeline-label small font-weight-bolder">{{ $t('Registration') }}:</span>
                            <span class="pipeline-timeline-value small ml-25">{{ formatShortDate(row.registrationDate) }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="pipeline-row-actions flex-shrink-0">
                        <b-button
                          variant="outline-primary"
                          class="pipeline-mail-btn btn-icon rounded-circle"
                          :disabled="!canSendPipelineMessage(row)"
                          v-b-tooltip.hover.top
                          :title="
                            canSendPipelineMessage(row)
                              ? $t('Send message')
                              : $t('No phone or linked student for messaging')
                          "
                          @click="openStudentMessageSidebar(row)"
                        >
                          <feather-icon icon="MailIcon" size="16" />
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>

            <b-col cols="12" lg="4">
              <div class="pipeline-column-card">
                <div class="pipeline-column-head">
                  <feather-icon icon="AwardIcon" size="16" class="mr-50" />
                  <span class="pipeline-column-title">{{ $t("Admissions") }}</span>
                  <span class="pipeline-column-count">{{ filteredAdmissions.length }}</span>
                </div>
                <div class="pipeline-column-filters d-flex align-items-center px-1 py-50 bg-light">
                  <b-form-checkbox
                    class="pipeline-column-select-all mb-0 flex-shrink-0"
                    :checked="pipelineColumnAllSelected('adm')"
                    :indeterminate="pipelineColumnSomeSelected('adm')"
                    :disabled="filteredAdmissions.length === 0"
                    v-b-tooltip.hover
                    :title="$t('Select all')"
                    @change="togglePipelineSelectAll('adm', $event)"
                  />
                  <flat-pickr
                    v-model="admissionDateFrom"
                    :config="columnDateConfig"
                    placeholder="From"
                    class="form-control form-control-sm mr-25 flex-grow-1"
                    @on-change="fetchPipeline"
                  />
                  <span class="mx-25 text-muted small flex-shrink-0">—</span>
                  <flat-pickr
                    v-model="admissionDateTill"
                    :config="columnDateConfig"
                    placeholder="Till"
                    class="form-control form-control-sm ml-25 flex-grow-1"
                    @on-change="fetchPipeline"
                  />
                </div>
                <div class="pipeline-column-body">
                  <div
                    v-if="filteredAdmissions.length === 0"
                    class="pipeline-empty"
                  >
                    {{ $t("No admissions in this range.") }}
                  </div>
                  <div v-else class="pipeline-scroll">
                    <div
                      v-for="(row, idx) in filteredAdmissions"
                      :key="'adm-' + idx + '-' + row.studentId"
                      class="pipeline-row-item pipeline-row-item--student"
                    >
                      <b-form-checkbox
                        class="pipeline-row-checkbox flex-shrink-0"
                        :checked="isPipelineSelected('adm', row, idx)"
                        @input="setPipelineSelected('adm', row, idx, $event)"
                      />
                      <div class="pipeline-student-block min-width-0">
                        <div class="d-flex align-items-center justify-content-between mb-25 pr-3">
                          <span class="pipeline-row-name text-truncate">{{ row.name || "—" }}</span>
                          <span class="pipeline-row-type-badge pipeline-row-type-badge--admission">{{ row.className || $t('Admission') }}</span>
                        </div>

                        <div class="d-flex flex-wrap align-items-center mt-25">
                          <div v-if="row.grNo" class="pipeline-row-chips mr-50">
                            <span class="pipeline-row-chip pipeline-row-chip--gr">{{ $t("GR") }} {{ row.grNo }}</span>
                          </div>
                          <div v-if="pipelineContactPhone(row)" class="pipeline-row-chips mr-50">
                            <span class="pipeline-row-chip pipeline-row-chip--phone text-truncate" :title="pipelineContactPhone(row)">
                              {{ pipelineContactPhone(row) }}
                            </span>
                          </div>
                        </div>

                        <div v-if="row.fatherName || row.contactFather" class="pipeline-compact-info mt-50">
                          <div v-if="row.fatherName" class="d-inline-flex align-items-center mr-1">
                            <feather-icon icon="UserIcon" size="12" class="text-muted mr-25" />
                            <span class="pipeline-info-value small">{{ row.fatherName }}</span>
                          </div>
                          <div v-if="row.contactFather" class="d-inline-flex align-items-center">
                            <feather-icon icon="PhoneIcon" size="12" class="text-muted mr-25" />
                            <span class="pipeline-info-value small">{{ row.contactFather }}</span>
                          </div>
                        </div>

                        <div class="pipeline-timeline horizontal mt-50">
                          <div v-if="row.admissionDate" class="pipeline-timeline-item">
                            <span class="pipeline-timeline-dot pipeline-timeline-dot--success"></span>
                            <span class="pipeline-timeline-label small font-weight-bolder">{{ $t('Admission') }}:</span>
                            <span class="pipeline-timeline-value small ml-25">{{ formatShortDate(row.admissionDate) }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="pipeline-row-actions flex-shrink-0">
                        <b-button
                          variant="outline-primary"
                          class="pipeline-mail-btn btn-icon rounded-circle"
                          :disabled="!canSendPipelineMessage(row)"
                          v-b-tooltip.hover.top
                          :title="
                            canSendPipelineMessage(row)
                              ? $t('Send message')
                              : $t('No phone or linked student for messaging')
                          "
                          @click="openStudentMessageSidebar(row)"
                        >
                          <feather-icon icon="MailIcon" size="16" />
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </template>

        <div v-else-if="!loading" class="pipeline-empty-state text-center py-4">
          <feather-icon icon="InboxIcon" size="48" class="text-muted mb-1" />
          <p class="mb-0 text-muted">{{ $t("No pipeline data loaded.") }}</p>
          <b-button variant="primary" class="mt-2" @click="fetchPipeline">
            {{ $t("Load") }}
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <b-sidebar
      id="pipeline-message-sidebar"
      v-model="messageSidebarVisible"
      bg-variant="white"
      sidebar-class="sidebar-lg pipeline-msg-sidebar"
      right
      backdrop
      shadow
      no-header
      @hidden="onMessageSidebarHidden"
    >
      <div class="pipeline-sidebar-header">
        <div class="pipeline-sidebar-header__main min-width-0">
          <h5 class="pipeline-sidebar-title mb-2">{{ $t("Send message") }}</h5>
          <div
            v-if="pipelineComposerBulk && pipelineTotalSelectionCount"
            class="pipeline-recipient-card pipeline-recipient-card--bulk mb-0"
          >
            <div class="pipeline-recipient-card__avatar pipeline-recipient-card__avatar--bulk">
              <feather-icon icon="UsersIcon" size="20" class="text-white" />
            </div>
            <div class="pipeline-recipient-card__body min-width-0">
              <span class="pipeline-recipient-name">
                {{ pipelineTotalSelectionCount }}
                {{ $t("selected") }}
              </span>
              <small
                v-if="
                  bulkRecipientsForSend.length !== pipelineTotalSelectionCount
                "
                class="text-muted d-block"
              >
                {{ bulkRecipientsForSend.length }}
                {{ $t("unique for messaging") }}
              </small>
              <div class="pipeline-bulk-recipient-chips">
                <span
                  v-for="entry in bulkSelectedEntries"
                  :key="entry.selectionKey"
                  class="pipeline-bulk-recipient-chip text-truncate"
                  :title="recipientSummary(entry.recipient)"
                >
                  {{ entry.recipient.name || "—" }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeMessageStudent"
            class="pipeline-recipient-card"
          >
            <div class="pipeline-recipient-card__avatar">
              <feather-icon icon="UserIcon" size="20" class="text-white" />
            </div>
            <div class="pipeline-recipient-card__body min-width-0">
              <span class="pipeline-recipient-name text-truncate">{{
                activeMessageStudent.name
              }}</span>
              <div class="pipeline-recipient-chips">
                <span
                  v-if="activeMessageStudent.grNo"
                  class="pipeline-recipient-chip pipeline-recipient-chip--gr"
                >
                  {{ $t("GR") }}
                  {{ activeMessageStudent.grNo }}
                </span>
                <span
                  v-if="activeMessageStudent.className"
                  class="pipeline-recipient-chip pipeline-recipient-chip--class text-truncate"
                  :title="activeMessageStudent.className"
                >
                  {{ activeMessageStudent.className }}
                </span>
                <span
                  v-if="
                    activeMessageStudent.contact &&
                    activeMessageStudent.messageChannel === 'whatsapp'
                  "
                  class="pipeline-recipient-chip pipeline-recipient-chip--phone text-truncate"
                  :title="activeMessageStudent.contact"
                >
                  {{ activeMessageStudent.contact }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <b-button
          variant="outline-secondary"
          size="sm"
          class="btn-icon rounded-circle flex-shrink-0 pipeline-sidebar-close"
          @click="messageSidebarVisible = false"
        >
          <feather-icon icon="XIcon" size="18" />
        </b-button>
      </div>
      <div class="pipeline-sidebar-body">
        <b-form-group
          :label="$t('Select template')"
          label-class="pipeline-sidebar-label"
          class="mb-2 pipeline-template-form-group"
        >
          <div v-if="templatesLoading" class="text-center py-2">
            <b-spinner small type="grow" variant="primary" />
          </div>
          <v-select
            v-else
            v-model="selectedTemplateId"
            :options="messageTemplates"
            label="title"
            :reduce="(t) => t.id"
            :clearable="true"
            :searchable="true"
            append-to-body
            :calculate-position="pipelineTemplateDropdownPosition"
            :placeholder="$t('Select template')"
            class="pipeline-template-select"
            @input="onTemplateSelected"
          >
            <template #option="option">
              <div class="pipeline-vs-option-row">
                <span class="pipeline-vs-option-title">{{
                  option.title
                }}</span>
                <span
                  v-if="option.sendOn && String(option.sendOn).trim()"
                  class="pipeline-vs-option-tag text-truncate"
                  :title="String(option.sendOn)"
                >
                  {{ option.sendOn }}
                </span>
              </div>
            </template>
            <template #selected-option="option">
              <span class="pipeline-vs-selected-title text-truncate">{{
                option.title
              }}</span>
            </template>
          </v-select>
        </b-form-group>
        <b-form-group
          :label="$t('Message')"
          label-class="pipeline-sidebar-label"
          class="mb-3"
        >
          <b-form-textarea
            v-model.trim="messageText"
            rows="8"
            :placeholder="$t('Enter your message')"
            class="pipeline-sidebar-textarea"
          />
        </b-form-group>
        <b-button
          variant="gradient-primary"
          block
          class="pipeline-sidebar-send-msg"
          :disabled="sending || !canSendForMode('message')"
          @click="sendComposer('message')"
        >
          <b-spinner v-if="sending" small class="mr-50" type="grow" />
          <template v-else>
            <feather-icon icon="SendIcon" size="16" class="mr-50" />
            {{ $t("Send message") }}
          </template>
        </b-button>
        <b-button
          variant="outline-warning"
          block
          class="mt-1 pipeline-sidebar-send-notify"
          :disabled="sending || !canSendForMode('notification')"
          :title="
            !canSendForMode('notification')
              ? $t('Notifications require a linked student record.')
              : undefined
          "
          @click="sendComposer('notification')"
        >
          <feather-icon icon="BellIcon" size="16" class="mr-50" />
          {{ $t("Send notification") }}
        </b-button>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BFormGroup,
  BFormCheckbox,
  BFormTextarea,
  BSidebar,
  BPopover,
  VBTooltip,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";

export default {
  name: "PipelineInsights",
  directives: {
    "b-tooltip": VBTooltip,
  },
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BFormGroup,
    BFormCheckbox,
    BFormTextarea,
    BSidebar,
    BPopover,
    flatPickr,
    vSelect,
  },
  props: {
    /** Campus filter sent in the pipeline API body */
    campusID: {
      type: Number,
      default: null,
    },
    /**
     * POST path relative to API domain (no leading slash).
     * Backend should accept body: { db, campusID, dateFrom, dateTill }
     * and return { status, data: { inquiryCount, registrations, … } }
     */
    pipelineEndpoint: {
      type: String,
      default: "StudentInquiries/GetAdmissionInquiryRegistrationStats",
    },
  },
  data() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    return {
      inquiryPriorityLegendTargetId:
        "pipeline-insights-inquiry-priority-legend",
      loading: false,
      pipeline: null,
      dateFromDay: fmt(start),
      dateTillDay: fmt(end),
      messageText: "",
      messageSidebarVisible: false,
      sending: false,
      messageTemplates: [],
      selectedTemplateId: null,
      activeTemplateMedia: "",
      activeMessageStudent: null,
      templatesLoading: false,
      pipelineSelection: {},
      pipelineComposerBulk: false,
      pipelineDatePickrConfig: {
        altFormat: "M j, Y",
        altInput: true,
        altInputClass: "form-control pipeline-date-flatpickr-input",
        dateFormat: "Y-m-d",
        allowInput: true,
      },
      followUpDateFrom: null,
      followUpDateTill: null,
      registrationDateFrom: null,
      registrationDateTill: null,
      admissionDateFrom: null,
      admissionDateTill: null,
      columnDateConfig: {
        altFormat: "M j",
        altInput: true,
        altInputClass: "form-control form-control-sm pipeline-column-date-input",
        dateFormat: "Y-m-d",
        allowInput: true,
      },
    };
  },
  computed: {
    effectiveCampusID() {
      return this.campusID != null
        ? this.campusID
        : this.$store.state.userData.cId;
    },
    dateFromIso() {
      return `${this.dateFromDay}T00:00:00`;
    },
    dateTillIso() {
      return `${this.dateTillDay}T23:59:59`;
    },
    canApplyRange() {
      return !!(this.dateFromDay && this.dateTillDay);
    },
    pipelineTotalSelectionCount() {
      return Object.keys(this.pipelineSelection || {}).length;
    },
    inquiryCount() {
      return Number(this.pipeline?.inquiryCount ?? 0);
    },
    registrationCount() {
      return Number(this.pipeline?.registrationCount ?? 0);
    },
    admissionCount() {
      return Number(this.pipeline?.admissionCount ?? 0);
    },
    filteredInquiries() {
      return this.filterInquiries(this.pipeline?.inquiries);
    },
    filteredRegistrations() {
      return this.filterStudentRows(this.pipeline?.registrations);
    },
    filteredAdmissions() {
      return this.filterStudentRows(this.pipeline?.admissions);
    },
    bulkSelectedEntries() {
      return Object.entries(this.pipelineSelection || {})
        .map(([selectionKey, item]) => {
          if (!item || !item.row) return null;
          return {
            selectionKey,
            kind: item.kind,
            row: item.row,
            recipient: this.normalizePipelineRecipient(item.row),
          };
        })
        .filter(Boolean);
    },

    /** Deduped recipients for API send (by student ID or phone). */
    bulkRecipientsForSend() {
      const seen = new Map();
      for (const entry of this.bulkSelectedEntries) {
        const r = entry.recipient;
        const row = entry.row;
        let dedupeKey;
        if (r.studentId > 0) {
          dedupeKey = `s:${r.studentId}`;
        } else {
          const phone = String(r.contact || "").trim();
          if (phone) dedupeKey = `p:${phone}`;
          else if (entry.kind === "inq" && row.id != null && row.id !== "") {
            dedupeKey = `inq:${row.id}`;
          } else {
            dedupeKey = entry.selectionKey;
          }
        }
        if (seen.has(dedupeKey)) continue;
        if (r.studentId > 0 || String(r.contact || "").trim()) {
          seen.set(dedupeKey, r);
        }
      }
      return Array.from(seen.values());
    },

    bulkRecipientsNormalized() {
      return this.bulkRecipientsForSend;
    },
    inquiryPriorityLegendLines() {
      return [
        {
          key: "hot",
          emoji: "🔥",
          title: "Hot (Urgent Action Needed)",
          criteria: [
            "Next follow-up is TODAY or overdue.",
            "OR follow-up count ≥ 3 AND next follow-up within 2 days.",
          ],
          action: "Drop everything — contact NOW.",
        },
        {
          key: "warm",
          emoji: "⚡",
          title: "Warm (Active Pipeline)",
          criteria: [
            "Follow-up count ≥ 1.",
            "Next follow-up scheduled within 3–7 days.",
          ],
          action: "Prepare and reach out as scheduled.",
        },
        {
          key: "cold",
          emoji: "❄️",
          title: "Cold (Needs Re-engagement)",
          criteria: [
            "Has follow-ups BUT last one was 8–15 days ago.",
            "OR next follow-up date passed but not marked dead.",
          ],
          action:
            "Send re-engagement message — warm them back up.",
        },
        {
          key: "dead",
          emoji: "💀",
          title: "Dead (Archive/Learn)",
          criteria: [
            "No response after 3+ follow-ups.",
            "OR last activity >15 days with no future follow-up."          ],
          action: "Archive — analyze why it died.",
        },
      ];
    },
  },
  watch: {
    dateFromDay() {
      this.fetchPipeline();
    },
    dateTillDay() {
      this.fetchPipeline();
    },
  },
  created() {
    this.loadMessageTemplates();
    this.fetchPipeline();
  },
  methods: {
    ...mapActions({
      post: "post",
      postObj: "postObj",
      get: "get",
    }),

    filterInquiries(list) {
      if (!Array.isArray(list)) return [];
      return list.filter((row) => {
        if (!row) return false;
        if (Number(row.id) > 0) return true;
        return !!(row.name && String(row.name).trim());
      });
    },

    filterStudentRows(list) {
      if (!Array.isArray(list)) return [];
      return list.filter((row) => row && Number(row.studentId) > 0);
    },

    formatShortDate(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },

    inquiryPrioritySlug(priority) {
      const s = String(priority || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");
      if (["hot", "warm", "cold", "dead"].includes(s)) return s;
      return "default";
    },

    inquiryPriorityLabel(priority) {
      return String(priority || "").trim().toUpperCase();
    },

    inquiryFollowupTooltip(row) {
      const n = Number(row && row.followupCount);
      if (!Number.isFinite(n) || n < 1) return this.$t("No follow-ups");
      return `${this.$t("Has follow-ups")} (${n})`;
    },

    /** First non-empty text field from inquiry API (message / notes / etc.). */
    inquiryMessagePreview(row) {
      if (!row || typeof row !== "object") return "";
      const keys = [
        "message",
        "Message",
        "remarks",
        "Remarks",
        "remark",
        "Remark",
        "notes",
        "Notes",
        "note",
        "Note",
        "description",
        "Description",
        "inquiryMessage",
        "InquiryMessage",
        "inquiryText",
        "InquiryText",
        "msg",
        "Msg",
        "detail",
        "Detail",
        "comment",
        "Comment",
        "comments",
        "Comments",
        "body",
        "Body",
        "text",
        "Text",
        "chatMessage",
        "ChatMessage",
        "lastMessage",
        "LastMessage",
        "visitorMessage",
        "VisitorMessage",
      ];
      for (let i = 0; i < keys.length; i++) {
        const v = row[keys[i]];
        if (v != null && String(v).trim()) return String(v).trim();
      }
      return "";
    },

    unwrapPipelineResponse(res) {
      if (!res) return null;
      if (typeof res === "object") {
        if (res.status === "success" && res.data) return res.data;
        const inner = res.data;
        if (inner && typeof inner === "object") {
          if (inner.status === "success" && inner.data) return inner.data;
          if (
            inner.inquiryCount != null ||
            Array.isArray(inner.inquiries) ||
            Array.isArray(inner.registrations)
          )
            return inner;
        }
        if (
          res.inquiryCount != null ||
          Array.isArray(res.inquiries) ||
          Array.isArray(res.registrations)
        )
          return res;
      }
      return null;
    },

    async fetchPipeline() {
      if (!this.canApplyRange) return;
      this.loading = true;
      try {
        const body = {
          db: this.$store.state.userData.db,
          campusID: this.effectiveCampusID,
          dateFrom: this.dateFromIso,
          dateTill: this.dateTillIso,
        };

        // Add column-level date filters
        if (this.followUpDateFrom) body.followUpDateFrom = this.followUpDateFrom + "T00:00:00";
        if (this.followUpDateTill) body.followUpDateTill = this.followUpDateTill + "T23:59:59";
        
        if (this.registrationDateFrom) body.registrationDateFrom = this.registrationDateFrom + "T00:00:00";
        if (this.registrationDateTill) body.registrationDateTill = this.registrationDateTill + "T23:59:59";
        
        if (this.admissionDateFrom) body.admissionDateFrom = this.admissionDateFrom + "T00:00:00";
        if (this.admissionDateTill) body.admissionDateTill = this.admissionDateTill + "T23:59:59";
        const url =
          this.$store.state.domain +
          this.pipelineEndpoint +
          "?db=" +
          encodeURIComponent(this.$store.state.userData.db);

        const raw = await this.post({
          url,
          body,
          token: this.$store.state.userData.token,
          context: this,
          subjects: true,
        });

        const data = this.unwrapPipelineResponse(raw);
        this.pipeline = data;
        if (!data) {
          this.$bvToast.toast(
            this.$t("Could not load pipeline data.") ||
              "Could not load pipeline data.",
            {
              title: this.$t("Error"),
              variant: "warning",
              solid: true,
            }
          );
        }
      } catch (e) {
        console.error(e);
        this.pipeline = null;
        this.$bvToast.toast(
          this.$t("Failed to load pipeline.") || "Failed to load pipeline.",
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.loading = false;
      }
    },

    async openStudentMessageSidebar(row) {
      const sid = this.pipelineStudentId(row);
      const phone = this.pipelineContactPhone(row);
      if (!sid && !phone) {
        this.$bvToast.toast(
          this.$t("No phone number or linked student for messaging."),
          {
            title: this.$t("Error"),
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      this.pipelineComposerBulk = false;
      this.activeMessageStudent = {
        studentId: sid || null,
        name: row.name || "",
        grNo: row.grNo || "",
        className: row.className || row.inquiryType || "",
        contact: phone || "",
        messageChannel: sid ? "student" : "whatsapp",
      };
      this.selectedTemplateId = null;
      this.activeTemplateMedia = "";
      this.messageText = "";
      this.messageSidebarVisible = true;
      this.templatesLoading = true;
      try {
        await this.loadMessageTemplates();
      } finally {
        this.templatesLoading = false;
      }
    },

    normalizePipelineRecipient(row) {
      const sid = this.pipelineStudentId(row);
      const phone = this.pipelineContactPhone(row);
      return {
        studentId: sid,
        contact: phone,
        messageChannel: sid ? "student" : "whatsapp",
        name: row.name || "",
        grNo: row.grNo || "",
        className: row.className || row.inquiryType || "",
      };
    },

    pipelineColumnRows(kind) {
      if (kind === "inq") return this.filteredInquiries;
      if (kind === "reg") return this.filteredRegistrations;
      return this.filteredAdmissions;
    },

    pipelineSelectionKey(kind, row, idx) {
      if (kind === "inq") return `inq:${row.id ?? "noid"}:${idx}`;
      if (kind === "reg") return `reg:${row.studentId}:${idx}`;
      return `adm:${row.studentId}:${idx}`;
    },

    pipelineColumnAllSelected(kind) {
      const list = this.pipelineColumnRows(kind);
      if (!list.length) return false;
      return list.every((row, idx) => this.isPipelineSelected(kind, row, idx));
    },

    pipelineColumnSomeSelected(kind) {
      const list = this.pipelineColumnRows(kind);
      if (!list.length) return false;
      let selected = 0;
      list.forEach((row, idx) => {
        if (this.isPipelineSelected(kind, row, idx)) selected += 1;
      });
      return selected > 0 && selected < list.length;
    },

    togglePipelineSelectAll(kind, checked) {
      const list = this.pipelineColumnRows(kind);
      list.forEach((row, idx) => {
        this.setPipelineSelected(kind, row, idx, !!checked);
      });
    },

    isPipelineSelected(kind, row, idx) {
      return !!this.pipelineSelection[this.pipelineSelectionKey(kind, row, idx)];
    },

    setPipelineSelected(kind, row, idx, checked) {
      const key = this.pipelineSelectionKey(kind, row, idx);
      if (checked) {
        this.$set(this.pipelineSelection, key, { kind, row });
      } else {
        this.$delete(this.pipelineSelection, key);
      }
    },

    clearPipelineSelection() {
      this.pipelineSelection = {};
    },

    recipientSummary(rec) {
      const parts = [rec.name, rec.grNo ? `GR ${rec.grNo}` : "", rec.contact || ""];
      return parts.filter(Boolean).join(" · ");
    },

    async openBulkComposerAllSelected() {
      if (this.pipelineTotalSelectionCount === 0) return;
      this.pipelineComposerBulk = true;
      this.activeMessageStudent = null;
      this.selectedTemplateId = null;
      this.activeTemplateMedia = "";
      this.messageText = "";
      this.messageSidebarVisible = true;
      this.templatesLoading = true;
      try {
        await this.loadMessageTemplates();
      } finally {
        this.templatesLoading = false;
      }
    },

    /** Student ID from pipeline row (registrations / admissions / some inquiries). */
    pipelineStudentId(row) {
      if (!row || typeof row !== "object") return 0;
      const v =
        row.studentId ??
        row.StudentId ??
        row.sID ??
        row.student_id ??
        row.sId;
      const n = Number(v);
      return n > 0 ? n : 0;
    },

    /** Phone / WhatsApp number from pipeline row. */
    pipelineContactPhone(row) {
      if (!row || typeof row !== "object") return "";
      const keys = [
        "contact",
        "Contact",
        "phone",
        "Phone",
        "mobile",
        "Mobile",
        "cell",
        "Cell",
        "telephone",
        "Telephone",
        "whatsapp",
        "WhatsApp",
        "fatherMobile",
        "FatherMobile",
        "guardianMobile",
        "GuardianMobile",
        "motherMobile",
        "MotherMobile",
      ];
      for (let i = 0; i < keys.length; i++) {
        const v = row[keys[i]];
        if (v != null && String(v).trim()) return String(v).trim();
      }
      return "";
    },

    canSendPipelineMessage(row) {
      return (
        this.pipelineStudentId(row) > 0 ||
        !!this.pipelineContactPhone(row)
      );
    },

    /** Unwrap MessageTemplate API — vuex get usually returns data.data; handle variants. */
    normalizeTemplateList(raw) {
      if (raw == null) return [];
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw.data)) return raw.data;
      if (
        raw.data &&
        typeof raw.data === "object" &&
        Array.isArray(raw.data.data)
      )
        return raw.data.data;
      if (raw.status === "success" && Array.isArray(raw.data)) return raw.data;
      return [];
    },

    async loadMessageTemplates() {
      try {
        const db = encodeURIComponent(this.$store.state.userData.db);
        const cID = this.$store.state.userData.cId;
        const res = await this.get({
          url:
            this.$store.state.domain +
            "MessageTemplate?db=" +
            db +
            "&cID=" +
            cID,
          token: this.$store.state.userData.token,
        });
        const list = this.normalizeTemplateList(res);

        const hasId = (el) => el != null && el.id != null && el.id !== "";

        const withLabel = list.filter(hasId).map((el) => ({
          ...el,
          title:
            (el.title && String(el.title).trim()) ||
            (el.name && String(el.name).trim()) ||
            `Template ${el.id}`,
        }));

        const manualOnly = withLabel.filter((el) => {
          const so = el.sendOn;
          if (so == null || so === undefined) return true;
          return String(so).trim() === "";
        });

        this.messageTemplates = manualOnly.length ? manualOnly : withLabel;
      } catch (e) {
        console.error("MessageTemplate load:", e);
        this.messageTemplates = [];
      }
    },

    resetMessageComposer() {
      this.selectedTemplateId = null;
      this.activeTemplateMedia = "";
      this.messageText = "";
      this.activeMessageStudent = null;
      this.templatesLoading = false;
      this.pipelineComposerBulk = false;
    },

    onMessageSidebarHidden() {
      if (this.sending) return;
      this.resetMessageComposer();
    },

    pipelineTemplateDropdownPosition(dropdownList, component, { width, top, left }) {
      dropdownList.classList.add("pipeline-template-dropdown-menu");
      dropdownList.style.top = top;
      dropdownList.style.left = left;
      dropdownList.style.width = width;
    },

    onTemplateSelected(templateId) {
      if (templateId == null || templateId === "") {
        this.messageText = "";
        this.activeTemplateMedia = "";
        return;
      }
      const t = this.messageTemplates.find(
        (el) => Number(el.id) === Number(templateId)
      );
      if (t) {
        this.messageText = t.message != null ? String(t.message) : "";
        this.activeTemplateMedia = t.media || "";
      }
    },

    canSendForMode(mode) {
      if (!this.messageText.trim()) return false;
      if (mode === "notification") {
        if (this.pipelineComposerBulk) {
          return this.bulkRecipientsNormalized.some((r) => r.studentId > 0);
        }
        return (
          Number(this.activeMessageStudent && this.activeMessageStudent.studentId) >
          0
        );
      }
      if (this.pipelineComposerBulk) {
        return this.bulkRecipientsNormalized.some(
          (r) => r.studentId > 0 || !!String(r.contact || "").trim()
        );
      }
      if (!this.activeMessageStudent) return false;
      const r = this.activeMessageStudent;
      const channel =
        r.messageChannel ||
        (Number(r.studentId) > 0 ? "student" : "whatsapp");
      if (channel === "whatsapp") {
        return !!String(r.contact || "").trim();
      }
      return Number(r.studentId) > 0;
    },

    async sendComposer(mode) {
      if (!this.canSendForMode(mode)) {
        if (!this.messageText.trim()) {
          this.$bvToast.toast(this.$t("Please enter message"), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
        return;
      }

      const notificationPayloadBase = () => ({
        audio: "",
        media: this.activeTemplateMedia || "",
        message: this.messageText,
        campusID: this.$store.state.userData.cId,
        userID: this.$store.state.userData.userID,
      });

      this.sending = true;
      try {
        if (mode === "notification") {
          let sIDs = [];
          if (this.pipelineComposerBulk) {
            sIDs = [
              ...new Set(
                this.bulkRecipientsNormalized
                  .filter((r) => r.studentId > 0)
                  .map((r) => Number(r.studentId))
              ),
            ];
          } else if (this.activeMessageStudent) {
            sIDs = [Number(this.activeMessageStudent.studentId)];
          }
          if (!sIDs.length) {
            this.$bvToast.toast(
              this.$t("No student recipients for notification."),
              {
                title: this.$t("Error"),
                variant: "warning",
                solid: true,
              }
            );
            return;
          }
          await this.postObj({
            url:
              this.$store.state.domain +
              "Chat/SendNotificationSelected?db=" +
              this.$store.state.userData.db,
            body: {
              ...notificationPayloadBase(),
              sIDs,
              tIDs: [],
            },
            message: this.$t("Notifications are being processed."),
            context: this,
            token: this.$store.state.userData.token,
          });
        } else if (this.pipelineComposerBulk) {
          const rec = this.bulkRecipientsNormalized;
          const studentIds = [
            ...new Set(
              rec.filter((r) => r.studentId > 0).map((r) => Number(r.studentId))
            ),
          ];
          const phones = [
            ...new Set(
              rec
                .filter((r) => !r.studentId && String(r.contact || "").trim())
                .map((r) => String(r.contact).trim())
            ),
          ];
          if (studentIds.length) {
            await this.postObj({
              url:
                this.$store.state.domain +
                "Chat/SendMessageSelected?db=" +
                this.$store.state.userData.db,
              body: {
                audio: "",
                media: this.activeTemplateMedia || "",
                message: this.messageText,
                sIDs: studentIds,
                tIDs: [],
                campusID: this.$store.state.userData.cId,
                userID: this.$store.state.userData.userID,
              },
              message: "Messages are being processed.",
              context: this,
              token: this.$store.state.userData.token,
            });
          }
          if (phones.length) {
            await this.postObj({
              url:
                this.$store.state.domain +
                "Chat/SendWhatsApp?db=" +
                this.$store.state.userData.db +
                "&cID=" +
                this.$store.state.userData.cId,
              body: {
                media: this.activeTemplateMedia || "",
                message: this.messageText,
                contacts: phones.join(", "),
                whatsapp: true,
                userID: this.$store.state.userData.userID,
              },
              message: "WhatsApp messages are being processed.",
              context: this,
              token: this.$store.state.userData.token,
            });
          }
        } else {
          const r = this.activeMessageStudent;
          const channel =
            r.messageChannel ||
            (Number(r.studentId) > 0 ? "student" : "whatsapp");
          if (channel === "whatsapp") {
            await this.postObj({
              url:
                this.$store.state.domain +
                "Chat/SendWhatsApp?db=" +
                this.$store.state.userData.db +
                "&cID=" +
                this.$store.state.userData.cId,
              body: {
                media: this.activeTemplateMedia || "",
                message: this.messageText,
                contacts: String(r.contact || "").trim(),
                whatsapp: true,
                userID: this.$store.state.userData.userID,
              },
              message: "WhatsApp messages are being processed.",
              context: this,
              token: this.$store.state.userData.token,
            });
          } else {
            const sid = Number(r.studentId);
            await this.postObj({
              url:
                this.$store.state.domain +
                "Chat/SendMessageSelected?db=" +
                this.$store.state.userData.db,
              body: {
                audio: "",
                media: this.activeTemplateMedia || "",
                message: this.messageText,
                sIDs: [sid],
                tIDs: [],
                campusID: this.$store.state.userData.cId,
                userID: this.$store.state.userData.userID,
              },
              message: "Messages are being processed.",
              context: this,
              token: this.$store.state.userData.token,
            });
          }
        }
        this.messageSidebarVisible = false;
        if (this.pipelineComposerBulk) {
          this.clearPipelineSelection();
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pipeline-insights-root {
  margin-bottom: 1.25rem;
}

::v-deep .pipeline-shell-card.card {
  border-radius: 20px;
  border: 1px solid #e0d8ff;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  overflow: hidden;
}

.pipeline-page-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.25rem;
  padding: 1.15rem 1.35rem 1rem !important;
  background: transparent !important;
  border-bottom: 2px solid #f0edff !important;
}

.pipeline-title-block {
  gap: 0.85rem;
}

.pipeline-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  box-shadow: 0 6px 18px rgba(124, 93, 250, 0.35);
}

.pipeline-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.35px;
}

.pipeline-subtitle {
  font-size: 0.82rem;
  font-weight: 600;
  color: #8a86b7;
}

.pipeline-date-group {
  min-width: 160px;
}

::v-deep .pipeline-date-group label {
  font-size: 0.72rem;
  font-weight: 800;
  color: #8a86b7;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0 !important;
  margin-right: 0.75rem;
  white-space: nowrap;
}

.pipeline-date-flatpickr-input {
  border-radius: 10px !important;
  border: 2px solid #e0d8ff !important;
  min-height: 38px;
  background: #fff !important;
}

.pipeline-filter-send-selected {
  border-radius: 10px;
  font-weight: 600;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center;
  gap: 0.4rem;
  line-height: 1.2;
}

.pipeline-filter-send-selected__label {
  display: inline-block;
  line-height: 1.2;
}

.pipeline-filter-send-selected__icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.pipeline-filter-send-selected__icon ::v-deep svg {
  display: block;
}

.pipeline-filter-clear-all {
  width: 2.25rem !important;
  height: 2.25rem !important;
  min-width: 2.25rem !important;
  padding: 0 !important;
  border-radius: 50% !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  line-height: 1 !important;
}

.pipeline-body {
  padding-top: 1rem !important;
}

.pipeline-stats-row {
  margin-left: -0.35rem;
  margin-right: -0.35rem;
}

.pipeline-stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border-radius: 16px;
  border: 1.5px solid #f0edff;
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  box-shadow: 0 6px 22px rgba(99, 102, 241, 0.1);
  height: 100%;
}

.pipeline-stat-card__icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  border: 2px solid #e0d8ff;
  color: #7c5dfa;
}

.pipeline-stat-card--registration .pipeline-stat-card__icon {
  color: #0369a1;
  border-color: #bae6fd;
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
}

.pipeline-stat-card--admission .pipeline-stat-card__icon {
  color: #059669;
  border-color: #a7f3d0;
  background: linear-gradient(180deg, #ffffff 0%, #ecfdf5 100%);
}

.pipeline-stat-card__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8a86b7;
  margin-bottom: 0.25rem;
}

.pipeline-stat-card__value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #2d1b69;
  line-height: 1.1;
}

.pipeline-columns-row {
  margin-left: -0.35rem;
  margin-right: -0.35rem;
}

.pipeline-column-card {
  border-radius: 16px;
  border: 1.5px solid #f0edff;
  background: #fff;
  box-shadow: 0 6px 22px rgba(99, 102, 241, 0.08);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 280px;
}

.pipeline-column-head {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.65rem 0.85rem;
  border-bottom: 2px solid #f0edff;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  color: #7c5dfa;
}

.pipeline-column-heading-main {
  row-gap: 0.25rem;
}

.pipeline-column-title--inline {
  font-weight: 800;
  font-size: 0.98rem;
  color: #2d1b69;
  line-height: 1.2;
}

.pipeline-priority-legend-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem;
  margin-left: 0.1rem;
  border-radius: 999px;
  cursor: help;
  color: #8b87ad;
  transition: color 0.15s ease, background 0.15s ease;
}

.pipeline-priority-legend-trigger:hover,
.pipeline-priority-legend-trigger:focus {
  color: #5b21b6;
  background: rgba(124, 93, 250, 0.12);
  outline: none;
}

.pipeline-priority-legend-trigger:focus-visible {
  box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.35);
}

.pipeline-column-title {
  font-weight: 800;
  font-size: 0.98rem;
  color: #2d1b69;
  flex: 1;
  min-width: 0;
}

.pipeline-column-count {
  font-size: 0.78rem;
  font-weight: 800;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #e0f2f7 0%, #d1e7f0 100%);
  color: #0369a1;
  border: 1px solid #bae6fd;
}

.pipeline-column-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.pipeline-scroll {
  flex: 1;
  max-height: 320px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0.35rem 0.5rem 0.65rem;
}

.pipeline-scroll::-webkit-scrollbar {
  width: 4px;
}

.pipeline-scroll::-webkit-scrollbar-thumb {
  background: #c4b5fd;
  border-radius: 999px;
}

.pipeline-row-item {
  padding: 0.55rem 0.45rem;
  border-radius: 10px;
  margin-bottom: 0.35rem;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease;
  overflow-x: hidden;
}

.pipeline-row-item:hover {
  background: #faf9ff;
  border-color: #ece9fb;
}

.pipeline-row-item--student {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.pipeline-row-checkbox {
  margin-top: 0.12rem !important;
}

.pipeline-row-checkbox ::v-deep label {
  margin-bottom: 0;
}

.pipeline-row-actions {
  flex-shrink: 0;
  align-self: flex-start;
  padding-top: 0.06rem;
}

.pipeline-recipient-card--bulk {
  align-items: flex-start;
}

.pipeline-recipient-card__avatar--bulk {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
}

.pipeline-bulk-recipient-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.35rem;
  max-height: 12rem;
  overflow-y: auto;
  padding-right: 0.2rem;
}

.pipeline-bulk-recipient-chip {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: #ede9fe;
  color: #5b21b6;
  border: 1px solid #ddd6fe;
  max-width: 140px;
}

.pipeline-student-block {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.pipeline-row-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.pipeline-row-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.24rem 0.55rem;
  border-radius: 999px;
  line-height: 1.2;
}

.pipeline-row-chip--gr {
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #c7d2fe;
}

.pipeline-row-chip--class {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.pipeline-row-chip--phone {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  max-width: 100%;
}

.pipeline-row-sub--inquiry {
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 600;
}

.pipeline-mail-btn:disabled {
  opacity: 0.42;
  box-shadow: none !important;
}

.pipeline-mail-btn {
  width: 2.35rem !important;
  height: 2.35rem !important;
  min-width: 2.35rem !important;
  padding: 0 !important;
  border-radius: 50% !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  line-height: 1 !important;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.15);
}

.pipeline-mail-btn:hover {
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  border-color: transparent !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.35);
}

.pipeline-sidebar-textarea {
  border-radius: 12px;
  border: 2px solid #e0d8ff;
}

::v-deep .pipeline-template-select .vs__dropdown-toggle {
  border-radius: 10px;
  border: 2px solid #e0d8ff;
  min-height: 38px;
}

::v-deep .pipeline-template-select .vs__search::placeholder {
  color: #9ca3af;
}

.pipeline-row-name {
  font-weight: 700;
  color: #2d1b69;
  font-size: 1rem;
  line-height: 1.35;
  display: block;
}

.pipeline-inquiry-title-stack {
  flex: 1;
  min-width: 0;
  gap: 0.4rem;
}

.pipeline-inquiry-title-stack .pipeline-row-name {
  flex: 1;
  min-width: 0;
}

.pipeline-inq-priority-tag {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.34rem 0.72rem;
  line-height: 1.15;
  border-radius: 999px;
  color: #fff;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    0 2px 6px rgba(0, 0, 0, 0.08),
    0 8px 18px rgba(0, 0, 0, 0.12);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.pipeline-row-item--inquiry:hover .pipeline-inq-priority-tag {
  transform: translateY(-1px);
}

.pipeline-inq-priority-tag--hot {
  background: linear-gradient(145deg, #fb7185 0%, #ef4444 45%, #dc2626 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    0 4px 16px rgba(239, 68, 68, 0.42),
    0 2px 8px rgba(220, 38, 38, 0.28);
}

.pipeline-inq-priority-tag--warm {
  background: linear-gradient(145deg, #60a5fa 0%, #3b82f6 45%, #2563eb 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    0 4px 16px rgba(59, 130, 246, 0.42),
    0 2px 8px rgba(37, 99, 235, 0.28);
}

.pipeline-inq-priority-tag--cold {
  background: linear-gradient(145deg, #67e8f9 0%, #22d3ee 40%, #0891b2 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 4px 16px rgba(34, 211, 238, 0.38),
    0 2px 8px rgba(8, 145, 178, 0.22);
}

.pipeline-inq-priority-tag--dead {
  background: linear-gradient(145deg, #71717a 0%, #52525b 40%, #27272a 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 4px 16px rgba(63, 63, 70, 0.38),
    0 2px 8px rgba(24, 24, 27, 0.35);
}

.pipeline-inq-priority-tag--default {
  background: linear-gradient(145deg, #a78bfa 0%, #8b5cf6 45%, #7c3aed 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 4px 14px rgba(139, 92, 246, 0.38);
}

.pipeline-row-meta {
  font-size: 0.8125rem;
  color: #4b5563;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 0.15rem;
}

.pipeline-row-sub {
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 600;
  margin-top: 0.25rem;
}

.pipeline-row-message {
  margin-top: 0.35rem;
  padding: 0.4rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.45;
  color: #374151;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 3px solid #c4b5fd;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pipeline-status-pill {
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  background: #ede9fe;
  color: #5b21b6;
  border: 1px solid #c4b5fd;
  flex-shrink: 0;
  align-self: flex-start;
}

.pipeline-status-pill--success {
  background: #d1fae5;
  color: #047857;
  border-color: #6ee7b7;
}

.pipeline-empty {
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #6b7280;
}

.pipeline-empty-state {
  padding: 1rem;
}

.pipeline-sidebar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1rem 0.65rem;
  border-bottom: 1px solid #f0edff;
}

.pipeline-sidebar-header__main {
  flex: 1;
  min-width: 0;
}

.pipeline-sidebar-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.pipeline-sidebar-close {
  margin-top: -0.15rem;
}

.pipeline-recipient-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #faf9ff 0%, #f4f2ff 100%);
  border: 1px solid #e8e4ff;
  box-shadow: 0 4px 14px rgba(124, 93, 250, 0.08);
}

.pipeline-recipient-card__avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.35);
}

.pipeline-recipient-card__body {
  flex: 1;
  min-width: 0;
  padding-top: 0.05rem;
}

.pipeline-recipient-name {
  display: block;
  font-size: 0.95rem;
  font-weight: 800;
  color: #2d1b69;
  line-height: 1.25;
  margin-bottom: 0.4rem;
}

.pipeline-recipient-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.pipeline-recipient-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  line-height: 1.2;
}

.pipeline-recipient-chip--gr {
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #c7d2fe;
}

.pipeline-recipient-chip--class {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.pipeline-recipient-chip--phone {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.pipeline-sidebar-label {
  font-size: 0.72rem !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8a86b7 !important;
  margin-bottom: 0.35rem !important;
}

.pipeline-template-form-group {
  position: relative;
}

::v-deep .pipeline-template-select .vs__selected-options {
  flex-wrap: nowrap;
  padding: 0.15rem 0 0.15rem 0.35rem;
}

.pipeline-vs-selected-title {
  display: inline-block;
  max-width: 100%;
  font-weight: 700;
  font-size: 0.875rem;
  color: #2d1b69;
  vertical-align: middle;
}

.pipeline-sidebar-body {
  padding: 1rem;
}

.min-width-0 {
  min-width: 0;
}

/* Enhanced Pipeline Row Items */
.pipeline-row-item {
  position: relative;
  background: #fff;
  border: 1.5px solid #f0f0f5;
  padding: 0.65rem 0.8rem;
  margin-bottom: 0.5rem;
  border-radius: 12px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
  display: flex;
  align-items: flex-start;
}

.pipeline-row-item:hover {
  border-color: #7c5dfa;
  background: #fafaff;
  box-shadow: 0 6px 18px rgba(124, 93, 250, 0.08);
  transform: translateY(-1px);
}

.pipeline-row-name {
  font-weight: 700;
  color: #2d1b69;
  font-size: 0.925rem;
  line-height: 1.2;
  display: block;
}

.pipeline-row-type-badge {
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  background: #f3f4f6;
  color: #4b5563;
  display: inline-block;
}

.pipeline-row-type-badge--admission {
  background: #ecfdf5;
  color: #059669;
}

.pipeline-row-type-badge--registration {
  background: #eff6ff;
  color: #2563eb;
}

.pipeline-compact-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.75rem;
}

.pipeline-info-label,
.pipeline-timeline-label {
  font-weight: 800;
  color: #94a3b8;
  text-transform: uppercase;
  font-size: 0.62rem;
  letter-spacing: 0.02em;
}

.pipeline-info-value {
  font-weight: 700;
  color: #475569;
  font-size: 0.76rem;
}

.pipeline-timeline {
  display: flex;
  gap: 0.75rem;
}

.pipeline-timeline.horizontal {
  flex-direction: row;
  flex-wrap: wrap;
}

.pipeline-timeline-one-line {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.45rem;
  min-width: 0;
  overflow: visible;
}

.pipeline-timeline-item--inline {
  display: inline-flex;
  flex-shrink: 0;
  max-width: 100%;
  white-space: nowrap;
}

.pipeline-timeline-inline-sep {
  user-select: none;
}

.pipeline-timeline-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.74rem;
}

.pipeline-timeline-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pipeline-timeline-dot--primary { background: #7c5dfa; }
.pipeline-timeline-dot--warning { background: #f59e0b; }
.pipeline-timeline-dot--success { background: #10b981; }
.pipeline-timeline-dot--info { background: #3b82f6; }

.pipeline-timeline-value {
  font-weight: 700;
  color: #64748b;
}

.pipeline-row-message {
  padding: 0.35rem 0.5rem;
  font-size: 0.74rem;
  font-weight: 500;
  color: #64748b;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 2px solid #e2e8f0;
}
</style>

<style lang="scss">
.popover.pipeline-priority-legend-popover {
  max-width: min(26rem, calc(100vw - 2rem));
  border: 1px solid #e0d8ff;
  box-shadow: 0 12px 36px rgba(45, 27, 105, 0.18);
}

.popover.pipeline-priority-legend-popover .popover-header {
  font-weight: 800;
  font-size: 0.875rem;
  color: #2d1b69;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border-bottom: 1px solid #f0edff;
}

.popover.pipeline-priority-legend-popover .popover-body {
  padding: 0.65rem 0.85rem 0.85rem;
}

.popover.pipeline-priority-legend-popover .pipeline-priority-legend-item + .pipeline-priority-legend-item {
  margin-top: 0.65rem;
}

.popover.pipeline-priority-legend-popover .pipeline-priority-legend-label {
  display: block;
  font-weight: 800;
  font-size: 0.8125rem;
  color: #374151;
  margin-bottom: 0.35rem;
  line-height: 1.35;
}

.popover.pipeline-priority-legend-popover .pipeline-priority-legend-emoji {
  margin-right: 0.25rem;
}

.popover.pipeline-priority-legend-popover .pipeline-priority-legend-bullets {
  margin: 0 0 0.4rem;
  padding-left: 1.1rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #64748b;
}

.popover.pipeline-priority-legend-popover .pipeline-priority-legend-bullets li {
  margin-bottom: 0.2rem;
}

.popover.pipeline-priority-legend-popover .pipeline-priority-legend-bullets li:last-child {
  margin-bottom: 0;
}

.popover.pipeline-priority-legend-popover .pipeline-priority-legend-action {
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #4338ca;
  font-weight: 600;
  border-top: 1px solid #f0edff;
  margin-top: 0.15rem;
  padding-top: 0.45rem;
}

.popover.pipeline-priority-legend-popover .pipeline-priority-legend-action strong {
  font-weight: 800;
  color: #2d1b69;
}

/* vue-select with append-to-body mounts the menu on document.body — scoped CSS cannot reach it */
.pipeline-template-dropdown-menu.vs__dropdown-menu {
  z-index: 10600 !important;
  border-radius: 12px !important;
  border: 1px solid #e0d8ff !important;
  box-shadow: 0 14px 42px rgba(45, 27, 105, 0.2) !important;
  margin-top: 4px !important;
  padding: 0.35rem 0 !important;
  max-height: 280px !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  background: #fff !important;
}

.pipeline-template-dropdown-menu .vs__dropdown-option {
  padding: 0 !important;
  margin: 0 0.35rem !important;
  border-radius: 10px !important;
  white-space: normal !important;
}

.pipeline-template-dropdown-menu .vs__dropdown-option--highlight {
  background: transparent !important;
}

.pipeline-template-dropdown-menu .vs__dropdown-option--highlight .pipeline-vs-option-row {
  background: #f5f3ff !important;
  border-radius: 10px;
}

.pipeline-template-dropdown-menu .pipeline-vs-option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.55rem 0.65rem;
  min-height: 2.55rem;
  border-radius: 10px;
}

.pipeline-template-dropdown-menu .pipeline-vs-option-title {
  flex: 1;
  min-width: 0;
  font-weight: 700;
  font-size: 0.875rem;
  color: #2d1b69;
  text-align: left;
  line-height: 1.3;
  word-break: break-word;
}

.pipeline-template-dropdown-menu .pipeline-vs-option-tag {
  flex-shrink: 0;
  max-width: 42%;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  background: #ede9fe;
  color: #5b21b6;
  border: 1px solid #ddd6fe;
}

.pipeline-template-dropdown-menu::-webkit-scrollbar {
  width: 5px;
}

.pipeline-template-dropdown-menu::-webkit-scrollbar-thumb {
  background: #c4b5fd;
  border-radius: 999px;
}

.pipeline-skeleton-item {
  background-color: #f1f5f9;
  width: 100%;
}

.skeleton-pulse {
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

.pipeline-column-filters {
  background: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0;
  gap: 0.35rem;
}

.pipeline-column-select-all {
  margin-right: 0.15rem;
}
.pipeline-column-select-all ::v-deep .custom-control-label::before,
.pipeline-column-select-all ::v-deep .custom-control-label::after {
  top: 0.1rem;
}

.pipeline-column-date-input {
  height: 32px !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #475569 !important;
  background-color: #fff !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  padding: 0 0.75rem !important;
  transition: all 0.2s ease !important;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.pipeline-column-date-input:hover {
  border-color: #94a3b8 !important;
}

.pipeline-column-date-input:focus {
  border-color: #7367f0 !important;
  background-color: #fff !important;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1) !important;
}

.pipeline-column-date-input::placeholder {
  color: #94a3b8;
  font-weight: 500;
}

.pipeline-stat-card--registration {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.25);
}

.font-small-1 {
  font-size: 0.7rem !important;
}
</style>
